import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { REGIONS, ENVS } from '@adsk/bim360-matrix-react-helpers/es/consts';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import {
  projectShape,
  accountShape,
  currentUserShape
} from '@adsk/bim360-matrix-react-helpers/es/propTypes';
import { MatrixLaunchDarklyProvider } from '@adsk/bim360-matrix-react-api/es/contexts/launchDarkly';
import { AccountProvider } from '@adsk/bim360-matrix-react-api/es/contexts/account';
import { ProjectProvider } from '@adsk/bim360-matrix-react-api/es/contexts/project';
import { EnvProvider } from '@adsk/bim360-matrix-react-api/es/contexts/env';
import { TokenProvider } from '@adsk/bim360-matrix-react-api/es/contexts/token';
import { IdleTimeProvider } from '@adsk/bim360-matrix-react-api/es/contexts/idleTime';
import { CurrentUserProvider } from '@adsk/bim360-matrix-react-api/es/contexts/currentUser';
import { NotificationProvider } from '@adsk/bim360-matrix-react-notification-manager/es/helpers/context';
import { ENV_CONFIGS, ENV_CONFIG_KEYS } from '@adsk/bim360-matrix-react-api/es/helpers/envConsts';
import { LocaleProvider } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { ThemeProvider } from '@adsk/bim360-matrix-react-helpers/es/theme';
import { deprecateProp } from '@adsk/bim360-matrix-react-helpers/es/deprecate';

/**
 * MatrixProvider component - a provider for Matrix API related data.
 * This provider allows using the following hooks/consumers beneath it:
 *
 * - useLocale, withLocale - can be used for translations.
 * - useEnv, useEnvConfig - getting current env, region and config based on them.
 * - useNotification - show toast & snackbar notifications.
 * - useToken - hook to get latest token.
 * - useAccount - for getting current account. Might be null on project scope pages.
 * - useProject - for getting current project. Might be null on account scope pages.
 * - useCurrentUser - for getting current logged in user data.
 */
const MatrixProvider = ({
  children,
  region,
  env,
  onTokenExpired,
  getToken,
  token,
  refreshToken,
  project,
  account,
  currentUser,
  locale,
  theme
}) => {
  const projectId = project ? project.id : undefined;
  const accountId = project ? project.accountId : account ? account.id : undefined;
  const analyticsId = currentUser ? currentUser.analyticsId : undefined;
  const dataReady = currentUser && (project || account);
  const ldUser = useMemo(
    () => (dataReady ? { key: analyticsId, custom: { region, accountId, projectId }} : null),
    [dataReady, analyticsId, region, accountId, projectId]
  );
  useEffect(() => {
    token && deprecateProp('MatrixProvider', 'token', 'getToken', new Date(2020, 6, 1));
  }, [token]);
  useEffect(() => {
    refreshToken &&
      deprecateProp('MatrixProvider', 'refreshToken', 'onTokenExpired', new Date(2020, 6, 1));
  }, [refreshToken]);

  if (!region) {
    throw new Error(`MatrixProvider error! region is required. Got: ${region}`);
  }
  if (!env) {
    throw new Error(`MatrixProvider error! env is required. Got: ${env}`);
  }
  const ldApiKey = ENV_CONFIGS[region][env][ENV_CONFIG_KEYS.LAUNCH_DARKLY_API_KEY];
  const getTokenMock = useCallback(() => Promise.resolve(token), [token]);
  const getTokenToUse = token ? getTokenMock : getToken;
  return (
    <LocaleProvider locale={locale}>
      <ThemeProvider theme={theme}>
        <EnvProvider region={region} env={env}>
          <NotificationProvider>
            <TokenProvider onTokenExpired={onTokenExpired || refreshToken} getToken={getTokenToUse}>
              <AccountProvider account={account}>
                <ProjectProvider project={project}>
                  <CurrentUserProvider currentUser={currentUser}>
                    <MatrixLaunchDarklyProvider apiKey={ldApiKey} user={ldUser}>
                      <IdleTimeProvider isFedRAMP={region === REGIONS.FEDRAMP}>
                        {children}
                      </IdleTimeProvider>
                    </MatrixLaunchDarklyProvider>
                  </CurrentUserProvider>
                </ProjectProvider>
              </AccountProvider>
            </TokenProvider>
          </NotificationProvider>
        </EnvProvider>
      </ThemeProvider>
    </LocaleProvider>
  );
};

MatrixProvider.Themes = ThemeProvider.Themes;
MatrixProvider.ENVS = ENVS;
MatrixProvider.REGIONS = REGIONS;

MatrixProvider.propTypes = {
  /**
   * The locale to be used by Matrix components.
   */
  locale: PropTypes.string,
  /**
   * The environment. One of `ENVS` values.
   */
  env: PropTypes.oneOf(objectValues(ENVS)).isRequired,
  /**
   * The region. One of `REGIONS` values.
   */
  region: PropTypes.oneOf(objectValues(REGIONS)).isRequired,
  /**
   * The selected project.
   * Might be null on account scope.
   */
  project: projectShape,
  /**
   * The selected account.
   * `account` is actually required, but when rendering it before the page loads we don't have it yet.
   */
  account: accountShape,
  /**
   * The current user.
   * `currentUser` is actually required, but when rendering it before the page loads we don't have it yet.
   */
  currentUser: currentUserShape,
  /**
   * Method to redirect user to login when token is expired. If not passed - user would redirect for DOCS to login.
   */
  onTokenExpired: PropTypes.func,
  /**
   * Needed only in when app doesn't have DOCS token.
   * Should return a promise resolving to a valid token.
   */
  getToken: PropTypes.func,
  /**
   * Deprecated. Use `getToken` if needed (or remove if using DOCS token).
   */
  token: PropTypes.string,
  /**
   * Deprecated. Use `getToken` & `onTokenExpired` if needed (or remove if using DOCS token).
   */
  refreshToken: PropTypes.func,
  /**
   * Theme in case you want to use Alloy. Default to Matrix.
   */
  theme: PropTypes.oneOf(objectValues(ThemeProvider.Themes)),
  children: PropTypes.node.isRequired
};

export default MatrixProvider;
